/**
 * `<NoteDetailsModal>` — read-only note metadata in the v3 modal idiom
 * (facelift-v3 guide Phase 6.3, the "NoteDetails (lg)" demo).
 *
 * Fetches the note via the same `get_note` tool the editor uses and renders a
 * `.modal-def-list` of its metadata + companion (extraction) counts. Pure
 * read-only — no mutations. Opened from the note row's right-click menu.
 */

import { useEffect, useState } from 'react';
import { Modal } from '@/components';
import { getNote } from '@/api';
import { useListNotebooksQuery, resolveNotebookPath } from '@/hooks/useNotebookQueries';
import type { NoteId, NoteView } from '@/types';

interface NoteDetailsModalProps {
  noteId: NoteId;
  /** Fallback title shown in the header until the fetch resolves. */
  title: string;
  onClose: () => void;
}

function fmt(ts: string | null | undefined): string {
  if (ts === null || ts === undefined || ts === '') return '—';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function NoteDetailsModal({ noteId, title, onClose }: NoteDetailsModalProps): JSX.Element {
  const [view, setView] = useState<NoteView | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setView(null);
    setError(null);
    getNote({ id: noteId })
      .then((resp) => {
        if (live) setView(resp.data);
      })
      .catch((err: unknown) => {
        if (live) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      live = false;
    };
  }, [noteId]);

  const note = view?.note;
  const companion = view?.companion ?? null;

  // Readable notebook directory path instead of the raw notebook UUID.
  const notebooksList = useListNotebooksQuery();
  const notebookPath = resolveNotebookPath(
    note?.notebookId ?? null,
    notebooksList.data?.items ?? [],
  );
  const notebookPathLabel =
    notebookPath.length > 0 ? `Notebooks / ${notebookPath.join(' / ')}` : '—';

  return (
    <Modal
      eyebrow="Note"
      title={note?.title ?? title}
      size="lg"
      onClose={onClose}
      footMeta="read-only · esc to close"
      footActions={
        <button type="button" className="btn btn--cool" onClick={onClose}>
          Done
        </button>
      }
    >
      {error !== null ? (
        <p className="modal__text" style={{ color: 'var(--bad)' }}>
          Couldn’t load note details: {error}
        </p>
      ) : view === null ? (
        <p className="modal__text">Loading…</p>
      ) : (
        <dl className="modal-def-list">
          <dt>Notebook</dt>
          <dd title={notebookPathLabel}>{notebookPathLabel}</dd>

          <dt>Source</dt>
          <dd>
            {note?.source ?? '—'}
            {note?.sourceId !== null && note?.sourceId !== undefined && (
              <small className="mono">{note.sourceId}</small>
            )}
          </dd>

          <dt>Created</dt>
          <dd>{fmt(note?.createdAt)}</dd>

          <dt>Updated</dt>
          <dd>
            {fmt(note?.updatedAt)}
            {note?.locallyModifiedAt != null && (
              <small>locally modified — not yet synced</small>
            )}
          </dd>

          <dt>State</dt>
          <dd>
            {note?.conflicted === true
              ? 'Conflicted'
              : note?.deletedAt != null
                ? 'Soft-deleted'
                : 'Live'}
            {note?.starred === true && <small>★ starred</small>}
          </dd>

          <dt>Extraction</dt>
          <dd>
            {companion === null ? (
              <span style={{ color: 'var(--fg-4)' }}>
                no companion record yet (extraction pending)
              </span>
            ) : (
              <span className="mono">
                {companion.chunks.length} chunks · {companion.entities.length} entities ·{' '}
                {companion.autoHashtags.length} tags · {companion.actionItems.length} actions ·{' '}
                {companion.topics.length} topics
              </span>
            )}
          </dd>

          <dt>Note ID</dt>
          <dd>
            <span className="mono">{noteId}</span>
          </dd>
        </dl>
      )}
    </Modal>
  );
}
